import { type FC, useCallback, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@stubhub/broadway-web/icons/functional";
import { Container } from "@viagogo/core/ui";
import type { EventListGridItem, ExploreCategoryCard } from "src/http/api-types";
import { useContent } from "src/shared/components/content";
import { useAppContext } from "src/shared/context/AppContext";
import { storeAnalyticsEvent } from "src/shared/utils/analyticsevent/analyticsevent-batcher";
import { formatTimestampForAnalytics } from "src/shared/utils/analyticsevent/utils";
import { TopResultCard } from "src/website-apps/responsive/stubhub-find/components/TopResults/TopResultCard";
import { SEARCH_CLICK_TYPES } from "src/website-apps/shared/utils/searchPageEvents";
import { ExploreEventItem } from "./ExploreEventItem";
import {
  card,
  cardsGrid,
  categoryHeaderWrapper,
  divider,
  eventItemHidden,
  eventItemVisible,
  eventsList,
  expandToggle,
} from "./ExploreModeCards.styled";

interface ExploreModeCardsProps {
  categories: readonly ExploreCategoryCard[];
}

export const ExploreModeCards: FC<ExploreModeCardsProps> = ({ categories }) => {
  const appContext = useAppContext();
  const { env } = appContext;
  const showMoreText = useContent("cid$#3405465#");
  const showLessText = useContent("cid$#3405464#");
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [animatingCategories, setAnimatingCategories] = useState(new Set());

  const handleCategoryClick = useCallback(
    (category: ExploreCategoryCard) => {
      const analyticsData = {
        anonymousId: appContext.anonymousId,
        pageVisitId: appContext.pageVisitId,
        userId: appContext.userId,
        cobrandId: appContext.cobrandId,
        currentUrl: appContext.currentUrl,
        sessionId: appContext.sessionId,
        entityType: 1, // Category
        entityId: category.categoryId,
        position: category.rank,
        queryId: category.queryId,
        recordId: category.objectId,
        indexName: category.indexName,
        searchClickType: SEARCH_CLICK_TYPES.RESULT_CATEGORY,
        clickDate: formatTimestampForAnalytics(),
      };
      storeAnalyticsEvent(
        {
          applicationName: appContext.appName,
          applicationVersion: appContext.appVersion,
          eventName: "SearchResultClick",
          eventBody: analyticsData,
          insertTime: Date.now(),
        },
        env,
      );
    },
    [appContext, env],
  );

  const handleEventClick = useCallback(
    (event: EventListGridItem, category: ExploreCategoryCard) => {
      const analyticsData = {
        anonymousId: appContext.anonymousId,
        pageVisitId: appContext.pageVisitId,
        userId: appContext.userId,
        cobrandId: appContext.cobrandId,
        currentUrl: appContext.currentUrl,
        sessionId: appContext.sessionId,
        entityType: 2, // Event
        entityId: event.eventId,
        position: category.rank,
        queryId: category.queryId,
        searchClickType: SEARCH_CLICK_TYPES.RESULT_EVENT,
        clickDate: formatTimestampForAnalytics(),
      };
      storeAnalyticsEvent(
        {
          applicationName: appContext.appName,
          applicationVersion: appContext.appVersion,
          eventName: "SearchResultClick",
          eventBody: analyticsData,
          insertTime: Date.now(),
        },
        env,
      );
    },
    [appContext, env],
  );

  const toggleExpanded = (categoryId: number | undefined) => {
    // Immediately update expanded state (triggers animation)
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });

    // Track animation state for label
    setAnimatingCategories((prev) => new Set(prev).add(categoryId));

    // Clear animation state after animation completes
    setTimeout(() => {
      setAnimatingCategories((prev) => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
    }, 250);
  };

  if (categories.length === 0) {
    return null;
  }

  return (
    <Container>
      <div className={cardsGrid}>
        {categories.map((category) => {
          return (
            <div key={category.categoryId} className={card}>
              <div className={categoryHeaderWrapper}>
                <TopResultCard
                  id={category.categoryId}
                  imageUrl={category.categoryImageUrl}
                  onClick={() => {
                    handleCategoryClick(category);
                  }}
                  title={category.categoryName}
                  subtitle={category.subtitle}
                  iconName={category.iconName}
                  numberRelationships={category.numberRelationships}
                  url={category.categoryUrl}
                  searchRedesign
                />
              </div>
              <hr className={divider} />

              {category.events && category.events.length > 0 && (
                <>
                  <div className={eventsList}>
                    {category.events.map((event, index) => (
                      <div
                        key={event.eventId}
                        className={
                          index < 3 || expandedCategories.has(category.categoryId) ? eventItemVisible : eventItemHidden
                        }
                      >
                        <ExploreEventItem
                          event={event}
                          onClick={() => {
                            handleEventClick(event, category);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  {category.events.length > 3 &&
                    (() => {
                      const isExpanded = expandedCategories.has(category.categoryId);
                      const isAnimating = animatingCategories.has(category.categoryId);

                      // During animation, show opposite state (old label/icon)
                      const showAsExpanded = isAnimating ? !isExpanded : isExpanded;
                      const label = showAsExpanded ? showLessText : showMoreText;
                      const Icon = showAsExpanded ? ChevronUpIcon : ChevronDownIcon;

                      return (
                        <>
                          <hr className={divider} />
                          <button
                            type="button"
                            className={expandToggle}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              toggleExpanded(category.categoryId);
                            }}
                            onKeyDown={(event) => {
                              if (!(event.key === "Enter" || event.key === " ")) {
                                return;
                              }

                              event.preventDefault();
                              event.stopPropagation();
                              toggleExpanded(category.categoryId);
                            }}
                          >
                            {label}
                            <span data-expand-icon="">
                              <Icon />
                            </span>
                          </button>
                        </>
                      );
                    })()}
                </>
              )}
            </div>
          );
        })}
      </div>
    </Container>
  );
};
